import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Play, Apple, ScanLine, ArrowDown } from "lucide-react";

const EXPO_URL = "https://skip-the-line-29.preview.emergentagent.com/";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, delay: 0.08 * i, ease: "easeOut" },
    }),
};

export const Hero = () => (
    <section id="top" className="relative overflow-hidden" data-testid="hero-section">
        {/* giant outline word in the background */}
        <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 select-none whitespace-nowrap text-[22vw] font-black uppercase leading-none text-outline opacity-[0.07]"
        >
            Quikpik
        </div>

        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 pb-24 pt-16 md:pt-24 lg:grid-cols-[1.15fr_0.85fr]">
            {/* Copy */}
            <div>
                <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0} className="mb-8 inline-block">
                    <span className="inline-block rounded-2xl bg-white px-5 py-3 hard-shadow-yellow">
                        <img
                            src="/quikpik-wordmark.png"
                            alt="Quikpik"
                            className="h-12 w-auto sm:h-14"
                            data-testid="hero-wordmark"
                        />
                    </span>
                </motion.div>

                <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1}
                    className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-primary"
                    data-testid="hero-eyebrow">
                    <ScanLine className="h-3.5 w-3.5" />
                    Pre-launch · Live on Expo Go
                </motion.div>

                <motion.h1
                    variants={fadeUp} initial="hidden" animate="show" custom={1}
                    className="mt-6 text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl"
                    data-testid="hero-headline"
                >
                    Skip the line.
                    <br />
                    <span className="text-primary">Just Quikpik.</span>
                </motion.h1>

                <motion.p
                    variants={fadeUp} initial="hidden" animate="show" custom={2}
                    className="mt-6 max-w-xl text-base leading-relaxed text-[#a1a1aa] md:text-lg"
                    data-testid="hero-subheadline"
                >
                    Pre-order your morning brew, walk in, grab from the express counter.
                    Zero wait, every time.
                </motion.p>

                {/* Store badges — intentionally disabled placeholders */}
                <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}
                    className="mt-10 flex flex-wrap items-center gap-4">
                    <button
                        type="button"
                        disabled
                        data-testid="play-store-button"
                        className="flex cursor-not-allowed items-center gap-3 rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] px-5 py-3 text-left opacity-60"
                    >
                        <Play className="h-6 w-6 text-[#6b6b6b]" fill="#6b6b6b" />
                        <span>
                            <span className="block text-[10px] font-medium uppercase tracking-widest text-[#6b6b6b]">Android</span>
                            <span className="block text-sm font-semibold text-[#8a8a8a]">Coming to Play Store</span>
                        </span>
                    </button>
                    <button
                        type="button"
                        disabled
                        data-testid="app-store-button"
                        className="flex cursor-not-allowed items-center gap-3 rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] px-5 py-3 text-left opacity-60"
                    >
                        <Apple className="h-6 w-6 text-[#6b6b6b]" fill="#6b6b6b" />
                        <span>
                            <span className="block text-[10px] font-medium uppercase tracking-widest text-[#6b6b6b]">iOS</span>
                            <span className="block text-sm font-semibold text-[#8a8a8a]">Coming soon</span>
                        </span>
                    </button>
                </motion.div>

                <motion.a
                    variants={fadeUp} initial="hidden" animate="show" custom={4}
                    href="#how-it-works"
                    data-testid="hero-how-it-works-link"
                    className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#a1a1aa] transition-colors hover:text-primary"
                >
                    See how it works <ArrowDown className="h-4 w-4" />
                </motion.a>
            </div>

            {/* QR card — the one live CTA */}
            <motion.div
                initial={{ opacity: 0, y: 32, rotate: 4 }}
                animate={{ opacity: 1, y: 0, rotate: 2 }}
                transition={{ duration: 0.6, delay: 0.25, ease: "easeOut" }}
                className="justify-self-center lg:justify-self-end"
            >
                <a
                    href={EXPO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="expo-go-qr-link"
                    className="block rounded-3xl bg-primary p-7 hard-shadow-black transition-transform duration-200 hover:rotate-0 hover:scale-[1.02] sm:p-9"
                >
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#111111]/70">Try it now on Expo Go</p>
                    <div className="mt-5 rounded-2xl bg-white p-4" data-testid="expo-go-qr-code">
                        <QRCodeSVG value={EXPO_URL} size={196} level="M" fgColor="#111111" bgColor="#FFFFFF" />
                    </div>
                    <p className="mt-5 text-center text-sm font-bold text-[#111111]">
                        Scan with your phone camera
                    </p>
                    <p className="mt-1 text-center text-xs font-medium text-[#111111]/60">
                        Opens the Quikpik app instantly
                    </p>
                </a>
            </motion.div>
        </div>
    </section>
);
